import React from 'react';
import { Quote, Star, MapPin, ArrowRight, PlayCircle } from 'lucide-react';
import luxuryVideo from '../../assets/Luxury_Home_Interior_Design_Video.mp4';
import luxuryVideo2 from '../../assets/Luxury_Home_Interior_Design_Video (1).mp4';

const CustomerStories = () => {
  
  // --- 1. COMPACT STORIES (Zigzag Data) ---
  const stories = [
    {
      id: 1,
      family: "The Kapoor Family",
      location: "New Delhi",
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=800",
      quote: "Seamless blend of heritage & luxury.",
      desc: "We wanted to preserve the old-world charm while upgrading with modern automation. The central courtyard redesign is the highlight."
    },
    {
      id: 2,
      family: "Arjun & Riya",
      location: "Gurgaon",
      image: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=800",
      quote: "A penthouse that rivals 7-star hotels.",
      desc: "The double-height ceiling in the living room was a challenge, but their custom chandelier installation connects both floors beautifully."
    },
    {
      id: 3,
      family: "Fernandes Villa",
      location: "Goa",
      image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=800",
      quote: "Tropical modernism spot on.",
      desc: "Living in London, we managed this remotely. When we walked in, it was exactly like the 3D renders, perhaps even better."
    }
  ];

  // --- 2. GRID TESTIMONIALS ---
  const gridReviews = [
    {
      name: "Linda P.",
      role: "Marketing Director",
      img: "https://randomuser.me/api/portraits/women/44.jpg",
      text: "Pixel perfect design delivered creatively. Truly captured our brand essence in the office space.",
      bg: "bg-white",
      height: "h-auto"
    },
    {
      name: "Michael B.",
      role: "Tech Founder",
      img: "https://randomuser.me/api/portraits/men/32.jpg",
      text: "Professional, timely, and their designs always exceed expectations. My home office is now a productivity haven.",
      bg: "bg-zinc-50",
      height: "h-auto" 
    },
    {
      name: "Jessica L.",
      role: "Fashion Designer",
      img: "https://randomuser.me/api/portraits/women/68.jpg",
      text: "I really appreciate the attention to texture and lighting. The walk-in wardrobe they designed is a dream come true.",
      bg: "bg-white",
      height: "h-auto"
    },
    {
      name: "Amit V.",
      role: "Architect",
      img: "https://randomuser.me/api/portraits/men/86.jpg",
      text: "Collaborating with them was a breeze. As an architect myself, I admire their precision in execution.",
      bg: "bg-zinc-50",
      height: "h-auto"
    },
    {
      name: "Sarah Jenkins",
      role: "Hotelier",
      img: "https://randomuser.me/api/portraits/women/22.jpg",
      text: "They transformed our boutique hotel lobby into an Instagram-worthy masterpiece. Our guests love it!",
      bg: "bg-white",
      height: "h-auto"
    },
    {
      name: "David Ross",
      role: "Real Estate Developer",
      img: "https://randomuser.me/api/portraits/men/54.jpg",
      text: "Visuals were stunning. The sample flat design helped us sell out the project in record time.",
      bg: "bg-white", 
      height: "h-auto"
    }
  ];

  return (
    <div className="bg-white min-h-screen text-zinc-900 font-sans selection:bg-amber-200 selection:text-black">
      
      {/* ================= HERO SECTION ================= */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-zinc-900">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80&w=2000" 
            alt="Client Diaries" 
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-black/80 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-black/80 to-transparent"></div>
        </div>

        <div className="relative z-10 text-center px-4 pt-10">
          <p className="text-amber-400 tracking-[0.3em] uppercase text-xs font-bold mb-3">Testimonials</p>
          <h1 className="text-5xl font-serif text-white mb-4 shadow-black drop-shadow-md">Client Diaries</h1>
          <div className="w-16 h-1 bg-amber-500 mx-auto rounded-full"></div>
        </div>
      </section>

      {/* ================= 2. ZIGZAG SECTION ================= */}
      <section className="py-16 px-4 bg-zinc-50/50">
        <div className="max-w-5xl mx-auto space-y-12">
           {stories.map((story, index) => (
             <div 
                key={story.id} 
                className={`flex flex-col md:flex-row items-center gap-0 bg-white shadow-md rounded-lg overflow-hidden border border-zinc-100 hover:shadow-lg transition-shadow duration-300 ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
             >
                <div className="md:w-2/5 w-full h-[300px] relative overflow-hidden">
                   <img src={story.image} alt={story.family} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
                <div className="md:w-3/5 p-8 flex flex-col justify-center relative">
                   <div className="flex gap-1 mb-3 text-amber-500">
                      {[1,2,3,4,5].map(i => <Star key={i} size={14} fill="currentColor" />)}
                   </div>
                   <h2 className="text-xl font-serif text-zinc-800 mb-3 leading-tight italic">"{story.quote}"</h2>
                   <p className="text-zinc-500 mb-4 text-sm leading-relaxed line-clamp-3">{story.desc}</p>
                   <div className="flex items-center gap-3 pt-4 border-t border-zinc-100">
                      <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">{story.family.charAt(0)}</div>
                      <div>
                         <h4 className="text-sm font-bold text-black uppercase tracking-wider">{story.family}</h4>
                         <p className="text-zinc-400 text-xs flex items-center gap-1"><MapPin size={10} /> {story.location}</p>
                      </div>
                   </div>
                </div>
             </div>
           ))}
        </div>
      </section>

      {/* ================= 3. GRID TESTIMONIALS ================= */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
               <h2 className="text-3xl font-serif mb-3 text-zinc-900">Loved by <span className="text-amber-600">Many</span></h2>
               <p className="text-zinc-500 text-sm">Real feedback from our valued clients.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {gridReviews.map((review, idx) => (
                 <div key={idx} className={`relative p-8 rounded-2xl shadow-lg border border-zinc-100 flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 ${review.bg}`}>
                    <Quote size={32} className={`mb-6 opacity-20 ${review.bg.includes('black') ? 'text-white' : 'text-black'}`} />
                    <p className={`text-sm leading-7 mb-8 font-medium ${review.bg.includes('black') ? 'text-zinc-300' : 'text-zinc-600'}`}>"{review.text}"</p>
                    <div className="flex items-center gap-4 mt-auto">
                       <img src={review.img} alt={review.name} className="w-12 h-12 rounded-full object-cover border-2 border-amber-500/30" />
                       <div>
                          <h4 className={`font-bold text-sm ${review.bg.includes('black') ? 'text-white' : 'text-zinc-900'}`}>{review.name}</h4>
                          <p className={`text-xs uppercase tracking-wider font-bold ${review.bg.includes('black') ? 'text-amber-500' : 'text-amber-600'}`}>{review.role}</p>
                       </div>
                    </div>
                    <div className="absolute top-8 right-8 flex gap-0.5">
                       {[1,2,3,4,5].map(i => <Star key={i} size={12} className="text-amber-400 fill-amber-400" />)}
                    </div>
                 </div>
               ))}
            </div>
        </div>
      </section>

      {/* ================= 4. CUSTOMER STORIES SECTION (DUAL CARDS) ================= */}
      <section className="py-32 px-4 bg-[#faf9f7]">
        <div className="max-w-6xl mx-auto">

          {/* ===== TOP HEADING ===== */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <p className="uppercase tracking-[0.35em] text-amber-600 text-xs mb-4">
              Real Homes. Real Stories.
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-zinc-900 leading-tight mb-6">
              Designed for the Way <span className="italic">You Live</span>
            </h2>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Hear directly from our clients as they share their experience of
              living in thoughtfully designed, timeless spaces.
            </p>
          </div>

          {/* ===== CARD 1: CONTENT LEFT | VIDEO RIGHT ===== */}
          <div className="bg-white rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.08)] overflow-hidden grid lg:grid-cols-2 mb-20">

            {/* Content Left */}
            <div className="p-14 flex flex-col justify-center">
              <p className="uppercase tracking-[0.35em] text-amber-600 text-xs mb-6">
                Client Story
              </p>
              <blockquote className="text-2xl font-serif text-zinc-900 leading-relaxed mb-10">
                “Luxuriya Homes understood our lifestyle perfectly.
                Every corner of our home now feels elegant, warm, and timeless.”
              </blockquote>
              <div className="flex items-center gap-4">
                <img
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2"
                  alt="Customer"
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-zinc-900">Ananya Sharma</p>
                  <p className="text-xs text-zinc-500">4 BHK Residence • Gurugram</p>
                </div>
              </div>
              <div className="mt-10">
                <button className="inline-flex items-center gap-3 uppercase tracking-widest text-xs font-semibold text-zinc-900 border-b border-zinc-900 hover:text-amber-600 hover:border-amber-600 transition-colors">
                  View More Stories <span>→</span>
                </button>
              </div>
            </div>

            {/* Video Right */}
            <div className="relative bg-black overflow-hidden h-[400px] lg:h-auto">
              <video className="w-full h-full object-cover" controls autoPlay loop muted playsInline>
                <source src={luxuryVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
            </div>
          </div>

          {/* ===== CARD 2: VIDEO LEFT | CONTENT RIGHT (MIRRORED) ===== */}
          <div className="bg-white rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.08)] overflow-hidden grid lg:grid-cols-2">

            {/* Video Left (Placed first in Grid) */}
            <div className="relative bg-black overflow-hidden h-[400px] lg:h-auto order-last lg:order-first">
               {/* Note: You can change the video source here for the second story */}
               <video className="w-full h-full object-cover" controls autoPlay loop muted playsInline>
                <source src={luxuryVideo2} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
            </div>

            {/* Content Right */}
            <div className="p-14 flex flex-col justify-center">
              <p className="uppercase tracking-[0.35em] text-amber-600 text-xs mb-6">
                Transformation
              </p>
              <blockquote className="text-2xl font-serif text-zinc-900 leading-relaxed mb-10">
                “From a bare shell to a masterpiece. The team turned our vision into a reality that exceeds all expectations.”
              </blockquote>
              <div className="flex items-center gap-4">
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d"
                  alt="Customer"
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-zinc-900">Vikram Malhotra</p>
                  <p className="text-xs text-zinc-500">Penthouse • Mumbai</p>
                </div>
              </div>
              <div className="mt-10">
                <button className="inline-flex items-center gap-3 uppercase tracking-widest text-xs font-semibold text-zinc-900 border-b border-zinc-900 hover:text-amber-600 hover:border-amber-600 transition-colors">
                  View Case Study <span>→</span>
                </button>
              </div>
            </div>

          </div>

        </div>
      </section>

    </div>
  );
};

export default CustomerStories;